// readState — may this query's data be stated to the operator as fact?
//
// The SCALAR companion to <QueryStateGate/>. The gate already solves the
// region case (a list, a table, a detail pane) and its own header spells out
// the contract: wrap ONLY the no-rows branch, and keep rendering from
// possibly-stale data so a background refetch never blanks the page. Nothing
// here changes that — for anything with rows, use the gate.
//
// What the gate cannot answer is the scalar case: a KPI tile, a count in a
// section header, whether an export button may be enabled. Those need one
// question answered — "do I actually have this number, or am I about to invent
// one?" — and getting it wrong is how a failed read becomes a confident zero.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHY THIS EXISTS (2026-08-25). The Phase B read-honesty sweep of the labour
// module was built as six parallel slices, and all six independently hand-rolled
// this predicate — `failedRead`, `canOffer`, `lock`, `checkFailed`, `ok`. Every
// one of them keyed off `!query.isSuccess`, and every one was wrong, because in
// react-query v5 a FAILED REFETCH ON A QUERY THAT STILL HOLDS GOOD DATA reports
// `status: 'error'`. Verified against @tanstack/react-query 5.101.0:
//
//     after a failed refetch:  status=error  isSuccess=false
//                              isError=true  data !== undefined  ← still there
//
// So `!isSuccess` is true while the screen holds perfectly good, seconds-old
// data. Six screens would have blanked their tiles to em-dashes and announced
// "couldn't read — not zero" on any transient refetch failure — replacing real
// numbers with dashes, which is worse than the bug the sweep set out to fix.
//
// The discriminator every one of them missed is `data !== undefined`, NOT the
// status flag. That is the entire reason this file exists, and the reason it is
// one shared tested function rather than six copies: the labour audit's central
// finding was that the module drifted precisely because primitives got
// hand-rolled per file instead of adopted.
// ─────────────────────────────────────────────────────────────────────────────

// Structural subset of UseQueryResult, mirroring QueryStateGate's QueryStateLike
// so a caller can pass the query object straight through with no generics, and
// a test can pass a plain object.
export interface ReadStateLike {
  isPending: boolean
  isError:   boolean
  isPaused:  boolean
  data?:     unknown
}

/**
 * `usable`  — we hold data. Render it. It may be stale (see `staleness`), but a
 *             real number the operator read a minute ago beats a dash.
 * `unread`  — we hold NO data and the read is not going to produce one right
 *             now (errored, or paused offline). NEVER state a fact from this:
 *             no counts, no zeros, no "all clear", no enabled exports.
 * `loading` — first load genuinely in flight. Show a pending affordance.
 */
export type ReadState = 'usable' | 'unread' | 'loading'

/**
 * Why the data on screen may not be current. `null` when it is trustworthy.
 * Only meaningful when `readState()` is `usable` — it describes data we HAVE.
 */
export type Staleness = 'refresh-failed' | 'offline' | null

const hasData = (q: ReadStateLike): boolean => q.data !== undefined

export function readState(q: ReadStateLike): ReadState {
  if (hasData(q)) return 'usable'
  // No data yet. A paused query is offline, not loading — react-query v5 keeps
  // `isPending` true while paused, so checking isPending first would render a
  // spinner forever on a cold offline start.
  if (q.isPaused || q.isError) return 'unread'
  return 'loading'
}

export function staleness(q: ReadStateLike): Staleness {
  if (!hasData(q)) return null      // nothing on screen to be stale
  if (q.isPaused) return 'offline'
  if (q.isError)  return 'refresh-failed'
  return null
}

/** True when the data may be stated to the operator as fact, unqualified. */
export function isTrustworthy(q: ReadStateLike): boolean {
  return readState(q) === 'usable' && staleness(q) === null
}

/**
 * ⚠️ CALLER NOTE — a DISABLED query defeats every gate below (2026-09-07).
 *
 * `enabled: false` leaves a query at status='pending' / fetchStatus='idle' with
 * no data, which is indistinguishable here from a first load in flight. So
 * `readState` says 'loading', `mustWithhold` returns FALSE and `countOrDash`
 * states the zero. That is deliberate — a query switched off because a modal is
 * shut or a filter is unset has not failed, and reporting it as 'unread' would
 * put "couldn't read" on every such screen. It is pinned by a premise test in
 * readState.test.ts that drives the real library.
 *
 * The trap is one level up. If the thing that disabled the query is ITSELF
 * derived from another read, then a failure of that upstream read silently
 * switches the gate off — exactly when you needed it. Found on Coldroom.tsx,
 * which already had the right gate and was bypassed anyway:
 *
 *     const { data: rooms = [] } = useQuery(...)          // fails -> []
 *     const effectiveRoomId = useMemo(..., [rooms])       // -> null
 *     const palletsQ = useQuery({ enabled: !!effectiveRoomId })   // disabled
 *     mustWithhold(palletsQ)                              // -> false. Gate off.
 *     // screen: "0 pallets across 0 cells", over a full room.
 *
 * Two remedies, both already used in this repo — copy one, don't invent a third:
 *   1. Gate on the upstream read too, or fold both into `combineReads(...)`.
 *      Payslip.tsx returns early on `mustWithhold(payslipQ)` before it ever
 *      consults the gates on the queries that payslip enables.
 *   2. Conjoin the enabling condition with the gate, so it is only asked when
 *      the query is actually on: Payslip.tsx:289 reads
 *      `ytdEnabled && mustWithhold(ytdQ)`, and Emp201.tsx:157 spells out the
 *      same idea for a SARS figure.
 *
 * <QueryStateGate/> has the matching hazard: a disabled query declines every
 * status branch and falls through to the caller's `empty` node, so it renders
 * "No X yet" for a read that never ran.
 */
/**
 * True when a factual claim, a zero, an "all clear" or an enabled export/print
 * must be withheld — i.e. we have no data at all. Note this is deliberately
 * NOT the negation of `isTrustworthy`: stale-but-present data is still data,
 * and blanking it is the regression this module exists to prevent.
 *
 * It covers the FAILED read only. A figure rendered unconditionally still shows
 * a confident 0 while the first read is in flight — on packhouse wifi a visible
 * false claim, not a flicker — so gate those on `readState(q) !== 'usable'`.
 * `=== 'unread'` alone is right only where a separate loading branch already
 * holds the figure back (e.g. <DocumentPrintGate>, which renders its own
 * 'Loading…').
 */
export function mustWithhold(q: ReadStateLike): boolean {
  return readState(q) === 'unread'
}

/**
 * A count for a tile or a header. Renders an em-dash rather than inventing a
 * zero when the number is not known. Pass the already-derived count — this
 * helper decides only whether you are allowed to show it.
 */
export function countOrDash(q: ReadStateLike, n: number): string {
  return mustWithhold(q) ? '—' : String(n)
}

/**
 * Combine several reads that feed ONE claim. The claim is only as good as its
 * weakest input: if any is unread the whole thing is unread, and any staleness
 * carries through. Use for a tile derived from two queries, or a "no-shows"
 * verdict that needs clocks AND roster AND leave before it can say "nobody".
 */
export function combineReads(...qs: ReadStateLike[]): { state: ReadState; stale: Staleness } {
  if (qs.some(q => readState(q) === 'unread'))  return { state: 'unread', stale: null }
  if (qs.some(q => readState(q) === 'loading')) return { state: 'loading', stale: null }
  const offline = qs.some(q => staleness(q) === 'offline')
  const failed  = qs.some(q => staleness(q) === 'refresh-failed')
  return { state: 'usable', stale: offline ? 'offline' : failed ? 'refresh-failed' : null }
}

/**
 * The sentence to put beside stale data. Deliberately short and operator-facing:
 * it sits under a tile, not in a banner. `null` when there is nothing to say.
 */
export function stalenessNote(s: Staleness): string | null {
  if (s === 'offline') return 'Offline — last known figure, not live.'
  if (s === 'refresh-failed') return 'Last refresh failed — this figure may have moved.'
  return null
}

/**
 * A PostgREST head-count, or a throw.
 *
 * `.select('id', { count: 'exact', head: true })` returns `count: null` when the
 * request fails, and the near-universal `count ?? 0` then turns that failure
 * into the number zero — which downstream prints as a fact: "QC samples across
 * this run: 0", "0 outstanding", "no shows". A count is a claim about how many
 * things exist, and a failed count is not a claim at all.
 *
 * Use inside a queryFn so the error reaches react-query and the surrounding
 * gate can withhold, instead of being absorbed into a plausible zero.
 * (packhouse UI audit 2026-08-28: CON-13 and its head-count siblings.)
 */
export function countOrThrow(res: { count: number | null; error: unknown }): number {
  if (res.error) throw res.error
  return res.count ?? 0
}
